import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * ScrollManager — single source of truth for scroll behaviour on route change.
 *
 * Behaviour:
 *  - Pathname change with NO hash → scroll to top (handles /equipe, /methode,
 *    /lexique, /blog, etc. — previously stuck at the bottom because users
 *    clicked them from the footer).
 *  - Pathname change WITH a hash (#simulator, #projects, …) → wait for the
 *    DOM to render, then smooth-scroll the target into view with a small
 *    header offset.
 *  - Hash-only change on the same page → smooth-scroll the new target.
 *  - Pure ?lang= search updates do not trigger scroll.
 */
const HEADER_OFFSET = 90;

const ScrollManager = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const id = hash.replace(/^#/, "");
      // Defer until the new page mounted its anchors
      const tries = [50, 200, 500];
      tries.forEach((delay) => {
        setTimeout(() => {
          const el = document.getElementById(id);
          if (!el) return;
          const top = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
          window.scrollTo({ top, behavior: "smooth" });
        }, delay);
      });
      return;
    }
    // No hash → top of page (instant — feels snappier than smooth on full nav)
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname, hash]);

  return null;
};

export default ScrollManager;
